import { format, parseISO, isValid } from 'date-fns';

/**
 * Safely parses a date value into a Date object.
 * @param {string|Date|null} value - The date value to parse.
 * @returns {Date|null} The parsed Date or null if invalid.
 */
export const safeParseDate = (value) => {
	if (!value) return null;
	try {
		const parsed = typeof value === 'string' ? parseISO(value) : new Date(value);
		return isValid(parsed) ? parsed : null;
	} catch (e) {
		return null;
	}
};

/**
 * Formats a date value for display (e.g., "30 November 2025").
 * @param {string|Date|null} value - The date value to format.
 * @param {string} formatStr - The date-fns format string. Defaults to 'dd MMMM yyyy'.
 * @returns {string} The formatted date string or empty string if invalid.
 */
export const formatDisplayDate = (value, formatStr = 'dd MMMM yyyy') => {
	const parsed = safeParseDate(value);
	return parsed ? format(parsed, formatStr) : '';
};

/**
 * Formats a date value for short display (e.g., "30 Nov 2025").
 * @param {string|Date|null} value - The date value to format.
 * @returns {string} The formatted date string or empty string if invalid.
 */
export const formatShortDate = (value) => {
	const parsed = safeParseDate(value);
	return parsed ? format(parsed, 'dd MMM yyyy') : '';
};

/**
 * Formats a date value for locale-aware display using browser's locale.
 * @param {string|Date|null} value - The date value to format.
 * @returns {string} The formatted date string or '—' if invalid.
 */
export const formatLocalizedDate = (value) => {
	if (!value) return '—';
	const date = safeParseDate(value);
	if (!date) return String(value);
	return date.toLocaleDateString();
};

/**
 * Formats a date range for display.
 * @param {string|Date|null} from - The start date.
 * @param {string|Date|null} to - The end date.
 * @param {string} formatStr - The date-fns format string. Defaults to 'dd MMMM yyyy'.
 * @returns {string} The formatted date range string.
 */
export const formatDateRange = (from, to, formatStr = 'dd MMMM yyyy') => {
	const fromDate = safeParseDate(from);
	const toDate = safeParseDate(to);
	if (!fromDate && !toDate) return '';
	if (fromDate && toDate && fromDate.getTime() === toDate.getTime()) {
		return format(fromDate, formatStr);
	}
	const start = fromDate ? format(fromDate, formatStr) : '';
	const end = toDate ? format(toDate, formatStr) : '';
	return [start, end].filter(Boolean).join(' - ');
};

/**
 * Gets today's date as an ISO string (YYYY-MM-DD).
 * @param {number} offsetDays - Number of days to add/subtract from today. Defaults to 0.
 * @returns {string} The ISO date string.
 */
export const getTodayIso = (offsetDays = 0) => {
	const base = new Date();
	base.setDate(base.getDate() + offsetDays);
	return base.toISOString().slice(0, 10);
};

/**
 * Adds days to a date and returns ISO string.
 * @param {string|Date|null} dateValue - The base date.
 * @param {number} days - Number of days to add.
 * @returns {string} The resulting ISO date string.
 */
export const addDaysToDate = (dateValue, days = 0) => {
	const parsed = safeParseDate(dateValue);
	if (!parsed) return getTodayIso(days);
	parsed.setDate(parsed.getDate() + days);
	return parsed.toISOString().slice(0, 10);
};



